import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Handshake, ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const FACTOR_LABELS = {
  meetings: 'Meetings & Calls',
  email_frequency: 'Email frequency (30d)',
  email_recency: 'Email recency',
  email_responsiveness: 'Owner reply time',
  email_balance: 'In/out balance',
};

function ScoreTooltip({ deal, weights }) {
  const b = deal.breakdown || {};
  return (
    <div className="space-y-1.5 w-60">
      <p className="text-xs font-medium text-foreground">Engagement score: {deal.score}/100</p>
      {Object.entries(FACTOR_LABELS).map(([key, label]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground w-32 shrink-0">{label}</span>
          <div className="flex-1 h-1 rounded-full bg-secondary overflow-hidden">
            <div className="h-full rounded-full bg-emerald-500/60" style={{ width: `${b[key] || 0}%` }} />
          </div>
          <span className="text-[10px] font-mono text-foreground w-14 text-right shrink-0">
            {b[key] ?? 0} ×{Math.round((weights?.[key] || 0) * 100)}%
          </span>
        </div>
      ))}
      <p className="text-[10px] text-muted-foreground pt-1 border-t border-border">
        {deal.olta} meetings/calls · {deal.emails_30d} mails/30d
        {deal.days_since_inbound != null ? ` · last reply ${deal.days_since_inbound}d ago` : ' · no inbound mail yet'}
        {deal.avg_reply_h != null ? ` · avg reply ${deal.avg_reply_h}h` : ''}
        {` · ${deal.inbound} in / ${deal.outbound} out`}
      </p>
    </div>
  );
}

export const OwnerEngagementWidget = () => {
  const navigate = useNavigate();
  const [top, setTop] = useState([]);
  const [weights, setWeights] = useState({});

  useEffect(() => {
    api.getOwnerEngagement().then(r => {
      setTop(r.data.top || []);
      setWeights(r.data.weights || {});
    }).catch(() => {});
  }, []);

  if (top.length === 0) return null;
  const max = Math.max(top[0]?.score || 1, 1);

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="owner-engagement-widget">
      <div className="px-4 pt-3 pb-1 flex items-center gap-2">
        <Handshake className="w-4 h-4 text-emerald-400" />
        <p className="font-heading text-base text-foreground">Top Owner Engagement</p>
        <span className="text-[10px] text-muted-foreground hidden sm:inline">
          — weighted score from owner meetings/calls + email signals (hover a score for the breakdown)
        </span>
      </div>
      <TooltipProvider delayDuration={150}>
        <div className="px-4 pb-3 pt-1 space-y-1">
          {top.map((d, i) => (
            <button key={d.project_id} onClick={() => navigate(`/projects/${d.project_id}`)}
              data-testid={`engagement-deal-${d.project_id}`}
              className="w-full flex items-center gap-3 px-2 py-1.5 rounded-sm hover:bg-secondary/40 transition-colors text-left group">
              <span className="text-[10px] font-mono text-muted-foreground/60 w-4 shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-foreground truncate">{d.name}</span>
                  {d.company && <span className="text-[10px] text-muted-foreground truncate hidden md:inline">{d.company}</span>}
                  <span className="text-[9px] font-mono text-blue-400/80 border border-blue-800/30 bg-secondary rounded px-1.5 py-0.5 shrink-0">{d.stage_label}</span>
                </div>
                <div className="h-1 rounded-full bg-secondary/50 overflow-hidden mt-1">
                  <div className="h-full rounded-full bg-emerald-500/50 transition-all duration-300"
                    style={{ width: `${(d.score / max) * 100}%` }} />
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-right shrink-0 cursor-help" data-testid={`engagement-score-${d.project_id}`}>
                    <span className="font-mono text-sm text-emerald-400 font-bold">{d.score}</span>
                    <p className="text-[9px] text-muted-foreground font-mono">
                      {d.total} mtg · {d.emails_30d} mail/30d{d.last_contact ? ` · ${d.last_contact}` : ''}
                    </p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="bg-card border-border p-3">
                  <ScoreTooltip deal={d} weights={weights} />
                </TooltipContent>
              </Tooltip>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors shrink-0" />
            </button>
          ))}
        </div>
      </TooltipProvider>
    </div>
  );
};
